import { z } from 'zod';
import type { CashFlowRequestOptions } from './cashFlow';

const cents = z.number().int().safe();
const entryRef = z.object({
  id: z.string(), party: z.string(), description: z.string().nullable(),
  expectedDate: z.string(), docNumber: z.string().nullable(), grossCents: cents,
});
const counterpart = z.object({
  id: z.string(), party: z.string(), description: z.string().nullable(),
  expectedDate: z.string(), docNumber: z.string().nullable(),
  source: z.string(), grossCents: cents,
});
export const reconciliationLinkSchema = z.object({
  linkId: z.string(), forecastEntryId: z.string(), actualEntryId: z.string(),
  forecastCents: cents, actualCents: cents,
  mode: z.enum(['replacement', 'partial']), matchSource: z.enum(['auto', 'manual']),
  matchReason: z.string().nullable(), createdAt: z.string(), counterpart,
});
export const reconciliationSchema = z.object({
  side: z.enum(['forecast', 'actual']).nullable(),
  originalForecastCents: cents.nullable(), allocatedForecastCents: cents,
  linkedActualCents: cents, remainingForecastCents: cents, varianceCents: cents,
  isFullyReconciled: z.boolean(), links: z.array(reconciliationLinkSchema),
});
export const suggestionSchema = z.object({
  id: z.string(), forecastEntryId: z.string(), actualEntryId: z.string(),
  proposedForecastCents: cents, proposedActualCents: cents,
  proposedMode: z.enum(['replacement', 'partial']), matchReason: z.string(),
  score: z.number(), status: z.enum(['pending', 'rejected']),
  forecast: entryRef, actual: entryRef,
});
export const reconciliationResponseSchema = z.object({
  suggestions: z.array(suggestionSchema),
  links: z.array(reconciliationLinkSchema.extend({
    forecast: entryRef,
    actual: entryRef,
  })),
});
export type Reconciliation = z.infer<typeof reconciliationSchema>;
export type ReconciliationSuggestion = z.infer<typeof suggestionSchema>;
export type ReconciliationLink = z.infer<typeof reconciliationLinkSchema>;
export type ReconciliationResponse = z.infer<typeof reconciliationResponseSchema>;
export type ReconciliationCandidate = z.infer<typeof entryRef>;
export interface ReconcileInput {
  suggestionId?: string; forecastEntryId: string; actualEntryId: string;
  forecastCents: number; actualCents: number; mode: 'replacement' | 'partial';
}
async function request(path: string, init: RequestInit, options: CashFlowRequestOptions = {}) {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetchImpl(`${baseUrl}${path}`, { ...init, headers });
  if (!res.ok) throw new Error(`Reconciliation request failed: ${res.status}`);
  return res.json();
}
export async function fetchReconciliations(options?: CashFlowRequestOptions): Promise<ReconciliationResponse> {
  return reconciliationResponseSchema.parse(await request('/api/forecast/reconciliations', {}, options));
}
export async function scanReconciliations(options?: CashFlowRequestOptions) {
  return request('/api/forecast/reconcile', { method: 'POST' }, options);
}
export async function confirmReconciliation(input: ReconcileInput, options?: CashFlowRequestOptions) {
  return request('/api/forecast/reconciliations/confirm', { method: 'POST', body: JSON.stringify(input) }, options);
}
export async function rejectReconciliation(id: string, options?: CashFlowRequestOptions) {
  return request(`/api/forecast/reconciliations/${id}/reject`, { method: 'POST' }, options);
}
export async function unlinkReconciliation(id: string, options?: CashFlowRequestOptions) {
  return request(`/api/forecast/reconciliations/${id}`, { method: 'DELETE' }, options);
}
export async function fetchReconciliationCandidates(entryId: string, options?: CashFlowRequestOptions): Promise<ReconciliationCandidate[]> {
  const data = await request(`/api/forecast/reconciliations/candidates?entryId=${encodeURIComponent(entryId)}`, {}, options);
  return z.object({ candidates: z.array(entryRef) }).parse(data).candidates;
}